// ════════════════════════════════════════════════════════════════
// CMS ACCESS — required OAP measures (track × report kind × diagnosis)
// ════════════════════════════════════════════════════════════════
// Source of truth: "ACCESS Model Payment Amounts and Performance Targets"
// (docs/business/access-model-payment-amts-performance-targets.pdf) — Table 3
// (eCKM, p9), Table 4 (CKM, p10-11), clinical validity windows p6.
//
// ⚠️ NOT the Operations Manual. Its per-track bullet list (p100) is flat and
// reads as though every CKM beneficiary owes eGFR/uACR; Table 4 gates them on a
// diabetes-or-CKD diagnosis. A flat list is what filed cms_submissions row 401
// for patient 23224837 with both absent — CMS accepted it, because CMS validates
// structure, not completeness. Spec: docs/specs/2026-08-17-cms-required-measure-gate.md
//
// Consumers: WF37 "Build FHIR Bundle" inlines the marker region VERBATIM (S-105
// sha-compares it); the app reaches it through app/src/logic/cmsRequiredMeasures.ts.
// The region is import-free on purpose — an n8n Code node cannot import — so
// DX_ALIASES is a deliberate minimal copy of the resolver's condition vocabulary,
// and S-105 asserts it agrees with access-subtrack-resolver.mjs token for token.
// ════════════════════════════════════════════════════════════════

/* CMS-OAP-MEASURES-START */

// Condition alias → diagnosis CLASS. Only the five classes the OAP tables gate on.
// Long-form slugs matter: care_plans.condition_slugs stores 'type-2-diabetes', not 't2d'.
const DX_ALIASES = {
  t2d: 'diabetes',
  diabetes: 'diabetes',
  'type 2 diabetes': 'diabetes',
  'type 2 diabetes mellitus': 'diabetes',
  t1d: 'diabetes',
  'type 1 diabetes': 'diabetes',
  'type 1 diabetes mellitus': 'diabetes',
  // Prediabetes is NOT diabetes for the kidney gate — Table 4 says "diabetes, CKD".
  prediabetes: 'prediabetes',
  'pre diabetes': 'prediabetes',
  ckd: 'ckd',
  'chronic kidney disease': 'ckd',
  'kidney disease': 'ckd',
  'chronic kidney disease (ckd)': 'ckd',
  ckd_stage_3a: 'ckd',
  'ckd stage 3a': 'ckd',
  'ckd 3a': 'ckd',
  'chronic kidney disease, stage 3a': 'ckd',
  ckd_stage_3b: 'ckd',
  'ckd stage 3b': 'ckd',
  'ckd 3b': 'ckd',
  'chronic kidney disease, stage 3b': 'ckd',
  // ESRD is CKD stage 5.
  esrd: 'ckd',
  'end stage renal disease': 'ckd',
  dyslipidemia: 'dyslipidemia',
  lipid: 'dyslipidemia',
  'high cholesterol': 'dyslipidemia',
  hyperlipidemia: 'dyslipidemia',
  'high triglycerides': 'dyslipidemia',
  'high cholesterol / high triglycerides': 'dyslipidemia',
  ascvd: 'ascvd',
  cvd: 'ascvd',
  cad: 'ascvd',
  'heart disease': 'ascvd',
  'cardiovascular disease': 'ascvd',
  'coronary artery disease': 'ascvd',
  'atherosclerotic cardiovascular disease': 'ascvd',
  'heart disease / cardiovascular disease': 'ascvd',
  // Known conditions that gate NO measure. Listed so they don't surface as
  // "unclassified" — an operator told we couldn't read 'hypertension' would
  // reasonably distrust the whole verdict.
  htn: 'other',
  hypertension: 'other',
  'high blood pressure': 'other',
  'essential hypertension': 'other',
  overweight: 'other',
  obesity: 'other',
  obese: 'other',
  heart_failure: 'other',
  'heart failure': 'other',
  'congestive heart failure': 'other',
  chf: 'other',
  afib: 'other',
  'atrial fibrillation': 'other',
  sleep_apnea: 'other',
  'sleep apnea': 'other',
  'obstructive sleep apnea': 'other',
  depression: 'other',
  osteoarthritis: 'other',
};

// Fixed windows (p6). hba1c/ldl are diagnosis-dependent — see windowDaysFor.
const WINDOW_FIXED = { systolic_bp: 15, weight: 15, bmi: 15, waist: 15, egfr: 365, uacr: 365 };

const KINDS = new Set(['baseline', 'quarterly', 'quarterly_bp', 'quarterly_weight', 'final']);

function dxKey(raw) {
  return String(raw ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Condition list → the five diagnosis classes the OAP tables gate on. Unrecognized
 *  entries are REPORTED, never silently treated as absent: a missed diagnosis makes a
 *  required measure look optional, which is the failure this module exists to prevent. */
export function classifyDiagnoses(conditions) {
  const arr = Array.isArray(conditions) ? conditions : conditions != null ? [conditions] : [];
  const found = new Set();
  const unclassified = [];
  for (const raw of arr) {
    if (raw == null) continue;
    const k = dxKey(raw);
    if (!k) continue;
    const hit = DX_ALIASES[k] || DX_ALIASES[k.replace(/[-_]+/g, ' ')] || null;
    if (hit) found.add(hit);
    else unclassified.push(String(raw));
  }
  return {
    diabetes: found.has('diabetes'),
    prediabetes: found.has('prediabetes'),
    ckd: found.has('ckd'),
    dyslipidemia: found.has('dyslipidemia'),
    ascvd: found.has('ascvd'),
    classes: Array.from(found).filter((c) => c !== 'other').sort(),
    unclassified,
  };
}

export function windowDaysFor(slug, dx) {
  const d = dx || {};
  if (slug === 'hba1c') return d.diabetes || d.prediabetes ? 365 : 730;
  if (slug === 'ldl') return d.dyslipidemia ? 365 : 730;
  return Object.hasOwn(WINDOW_FIXED, slug) ? WINDOW_FIXED[slug] : null;
}

/**
 * Which measures CMS REQUIRES for this beneficiary and this report, which it merely
 * recommends, and which are optional.
 *
 * `dx_available: false` means the conditions could not be read. Every
 * diagnosis-CONDITIONAL requirement then degrades to `recommended` and the caller gets
 * `dx_available: false` to surface — the diagnosis-INDEPENDENT requirements still bind.
 * A degraded read must not manufacture a requirement (it would block a filing over an
 * infra blip, and past day 60 that unaligns the beneficiary) nor silently drop one.
 */
export function requiredMeasures({ track, conditions, report_kind, dx_available = true } = {}) {
  const t = String(track ?? '').trim().toLowerCase() === 'ckm' ? 'CKM' : 'eCKM';
  const kind = KINDS.has(String(report_kind ?? '').trim().toLowerCase())
    ? String(report_kind).trim().toLowerCase()
    : 'baseline';
  const dx = classifyDiagnoses(conditions);

  const required = [];
  const recommended = [];
  const why = {};
  const req = (slug, reason) => { required.push(slug); why[slug] = reason; };
  const rec = (slug, reason) => { recommended.push(slug); why[slug] = reason; };
  // A diagnosis-conditional requirement: binds when the dx is known AND present.
  const conditional = (slug, due, reqReason, recReason) => {
    if (!dx_available) rec(slug, 'diagnosis unavailable — requirement could not be evaluated');
    else if (due) req(slug, reqReason);
    else rec(slug, recReason);
  };

  if (kind === 'quarterly_bp') {
    req('systolic_bp', 'required: quarterly BP (systolic + diastolic)');
  } else if (kind === 'quarterly_weight') {
    req('weight', 'required: quarterly weight');
    req('bmi', 'required: quarterly BMI');
  } else {
    req('systolic_bp', `required: ${t} ${kind} BP (systolic + diastolic)`);
    req('weight', `required: ${t} ${kind} weight`);
    req('bmi', `required: ${t} ${kind} BMI`);

    if (kind === 'baseline') {
      req('hba1c', 'required: baseline HbA1c, all beneficiaries');
      req('ldl', 'required: baseline LDL-C, all beneficiaries');
      if (t === 'CKM') {
        const kidneyDue = dx.diabetes || dx.ckd;
        const label = dx.diabetes && dx.ckd ? 'diabetes + CKD' : dx.diabetes ? 'diabetes' : 'CKD';
        conditional('egfr', kidneyDue, `required: CKM + ${label}`, 'not required: CKM without diabetes/CKD');
        conditional('uacr', kidneyDue, `required: CKM + ${label}`, 'not required: CKM without diabetes/CKD');
      }
    }

    if (kind === 'final') {
      const a1cDue = t === 'CKM' ? dx.diabetes : dx.prediabetes;
      conditional('hba1c', a1cDue,
        `required: ${t} end-of-period + ${t === 'CKM' ? 'diabetes' : 'prediabetes'}`,
        `not required: ${t} end-of-period without ${t === 'CKM' ? 'diabetes' : 'prediabetes'}`);
      const ldlDue = t === 'CKM' ? dx.dyslipidemia || dx.ascvd : dx.dyslipidemia;
      conditional('ldl', ldlDue,
        `required: ${t} end-of-period + ${dx.dyslipidemia ? 'dyslipidemia' : 'ASCVD'}`,
        `not required: ${t} end-of-period without ${t === 'CKM' ? 'dyslipidemia/ASCVD' : 'dyslipidemia'}`);
      if (t === 'CKM') {
        // Ops Manual p100: baseline-only measures, but worth including when a follow-on
        // period is expected — that submission can serve as its baseline.
        rec('egfr', 'recommended at end-of-period when a follow-on period is expected');
        rec('uacr', 'recommended at end-of-period when a follow-on period is expected');
      }
    }
  }

  const windowDays = {};
  for (const slug of [...required, ...recommended, 'waist']) windowDays[slug] = windowDaysFor(slug, dx);

  return {
    track: t,
    report_kind: kind,
    required,
    recommended,
    optional: ['waist'],
    why,
    windowDays,
    dx,
    dx_available: !!dx_available,
  };
}

/**
 * The gate. `present` = slugs the bundle resolved a value for; `collectedAt` = {slug: ISO}
 * where a REAL collection date is known.
 *
 * Window verdicts are only issued for measures whose collection date is known. WF37 stamps
 * `effectiveDateTime = today` when a value comes from measure_state with no date, so
 * trusting that would make the window check decorative, and treating unknown as invalid
 * would block nearly every patient. Unknown lands in `window_unverifiable` instead.
 */
export function evaluateRequiredMeasures({
  track, conditions, report_kind, dx_available = true,
  present = [], collectedAt = {}, now = null,
} = {}) {
  const spec = requiredMeasures({ track, conditions, report_kind, dx_available });
  const have = new Set(Array.isArray(present) ? present : []);
  const nowMs = now == null ? Date.now() : (typeof now === 'number' ? now : Date.parse(now));

  const missing = [];
  const invalid_window = [];
  const window_unverifiable = [];

  for (const slug of spec.required) {
    if (!have.has(slug)) { missing.push({ slug, why: spec.why[slug] }); continue; }
    const limit = spec.windowDays[slug];
    if (limit == null) continue;
    const at = Date.parse(collectedAt?.[slug] ?? '');
    if (!Number.isFinite(at)) { window_unverifiable.push({ slug, limit_days: limit }); continue; }
    const ageDays = Math.floor((nowMs - at) / 86400000);
    // p6: validity is collection→submission. Out of window is INVALID, i.e. as good as missing.
    if (ageDays > limit) invalid_window.push({ slug, age_days: ageDays, limit_days: limit, collected_at: collectedAt[slug] });
  }

  const blockers = missing.length + invalid_window.length;
  return {
    ...spec,
    missing,
    invalid_window,
    window_unverifiable,
    ok: blockers === 0,
    reason: blockers === 0 ? null : [
      missing.length ? `missing required: ${missing.map((m) => m.slug).join(', ')}` : null,
      invalid_window.length ? `outside validity window: ${invalid_window.map((w) => `${w.slug} (${w.age_days}d > ${w.limit_days}d)`).join(', ')}` : null,
    ].filter(Boolean).join(' · '),
  };
}

/** An override unlocks the gate only with a real, human-typed justification. */
export const OVERRIDE_MIN_REASON = 20;
export function overrideAccepted(override) {
  const reason = String(override?.reason ?? '').trim();
  return reason.length >= OVERRIDE_MIN_REASON;
}

/* CMS-OAP-MEASURES-END */
